import { defineStore } from 'pinia';
import { fetchWrapper } from '@/helpers';
import { useAlertStore } from '..';
import { updateLicenses } from '../account.store';
import { updateStatistics } from './statistics.store';

const baseUrl = `${process.env.VUE_APP_VUE_API_URL}`;

export const useAdminLicenseStore = defineStore('admin-license', {
    state: () => ({
        region: 'all',
        regionCounts: null,
        Licenses:[],
        customers:[],
        loading: false
    }),
    actions: {
        async loadLicenses(data) {
            const alertStore = useAlertStore();
            try {
                this.loading  = true
                // the UK / USA / Other tab is kept here, so every reload on the page uses it
                const res = await fetchWrapper.post(`${baseUrl}/admin/license/load`, { ...data, region: this.region });
                if (res.regionCounts) this.regionCounts = res.regionCounts;
                if(data.from === 0 || !data.from)
                    this.Licenses = res.totalLicenses
                else this.Licenses.push(...res.totalLicenses);
                updateStatistics()
                this.loading = false
                } catch (error) {
                    this.loading = false;
                    alertStore.error(error);
                }
        },
        async updateLicense(updateRequest) {
            const alertStore = useAlertStore();
            try {
                await fetchWrapper.post(`${baseUrl}/admin/license/update`, { updateRequest });
                alertStore.success("Update successful")

            } catch (error) {
                alertStore.error(error);
            }
        },
        async changeIp(uuid) {
            const alertStore = useAlertStore();
            try {
                await fetchWrapper.post(`${baseUrl}/admin/license/change-ip`, { license:uuid });
                alertStore.success("Changed IP")

            } catch (error) {
                alertStore.error(error);
            }
        },
        async loadIpChangeHistory(license_uuid, data = {}) {
            const alertStore = useAlertStore();
            try {
                const res = await fetchWrapper.post(`${baseUrl}/admin/license/ip-change-history`, {
                    license_uuid,
                    from: data.from || 0,
                    count: data.count || 50,
                });
                return res;
            } catch (error) {
                alertStore.error(error);
                return { histories: [], total: 0 };
            }
        },
        async reboot(uuid) {
            const alertStore = useAlertStore();
            try {
                await fetchWrapper.post(`${baseUrl}/admin/license/reboot`, { license:uuid });
                alertStore.success("Reboot Proxy successed")

            } catch (error) {
                alertStore.error(error);
            }
        },
        async getModemList(license_uuid) {
            const alertStore = useAlertStore();
            try {
                const res = await fetchWrapper.post(`${baseUrl}/admin/license/get-modem-list`, { license_uuid });
                return res.modemList
            } catch (error) {
                alertStore.error(error);
                return []
            }
        },
        async getProxiesAndPackages(serverName) {
            const alertStore = useAlertStore();
            try {
                const res = await fetchWrapper.post(`${baseUrl}/admin/license/get-proxy-package-list`, { serverName });
                return {proList: res.proxyList, pkgList:res.packageList}
            } catch (error) {
                alertStore.error(error);
                return {proList: [], pkgList: []}
            }
        },
        async changeModem(updateRequest) {
            const alertStore = useAlertStore();
            try {
                const res = await fetchWrapper.post(`${baseUrl}/admin/license/change-modem`, { updateRequest });
                alertStore.success(res?.message && res.message !== "Success" ? res.message : "Changed Modem")
                return true
            } catch (error) {
                alertStore.error(error);
            }
        },
        async createLicense(createRequest) {
            const alertStore = useAlertStore();
            try {
                await fetchWrapper.post(`${baseUrl}/admin/license/create`, { createRequest });
                updateLicenses()
                alertStore.success("Created new License")
                return
            } catch (error) {
                alertStore.error(error);
            }
        },
        async getClientList() {
            const alertStore = useAlertStore();
            try {
                const res = await fetchWrapper.get(`${baseUrl}/admin/license/get-clients`);
                return res.userList
            } catch (error) {
                alertStore.error(error);
            }
        },
        async deleteLicenses(uuids) {
            const alertStore = useAlertStore();
            try {
                await fetchWrapper.post(`${baseUrl}/admin/license/delete`, { uuids });
                updateLicenses()
                alertStore.success("Delete successful")
            } catch (error) {
                alertStore.error(error);
            }
        },
        // quick add: free ports across all servers
        async getAvailablePorts() {
            try { const res = await fetchWrapper.get(`${baseUrl}/admin/license/available-ports`); return res.ports || []; }
            catch (error) { useAlertStore().error(error); return []; }
        },
        async quickCreateLicense(payload) {
            const alertStore = useAlertStore();
            try {
                const res = await fetchWrapper.post(`${baseUrl}/admin/license/quick-create`, payload);
                updateLicenses()
                alertStore.success("License added to the customer")
                return res || true   // carries { uuid, credited, supplier_error } when a supplier top-up was booked
            } catch (error) { alertStore.error(error); return false }
        },
        // quick extend from the row + button: amount in hours/days/weeks/months, no charge
        async extendTime(uuid, amount, unit, expired_date = null, supplier_amount = 0, customer_amount = 0, no_supplier_credit = false) {
            const alertStore = useAlertStore();
            try {
                const res = await fetchWrapper.post(`${baseUrl}/admin/license/extend-time`, { uuid, amount, unit, expired_date, supplier_amount, customer_amount, no_supplier_credit });
                updateLicenses()
                const parts = []
                if (res?.charged) parts.push(`$${Number(res.charged.amount).toFixed(2)} taken from the customer (balance now $${Number(res.charged.balance).toFixed(2)})`)
                if (res?.credited) parts.push(`$${Number(res.credited.amount).toFixed(2)} credited to ${res.credited.name}`)
                if (res?.customer_error) alertStore.error("Time added, but the customer charge failed: " + res.customer_error)
                if (res?.supplier_error) alertStore.error("Time added, but the supplier top-up failed: " + res.supplier_error)
                if (!res?.customer_error && !res?.supplier_error) alertStore.success(parts.length ? "Time added; " + parts.join("; ") : "Time added to the license")
                return res || true
            } catch (error) { alertStore.error(error); return false }
        },
        // supplier + cost price of a licence's package (null when it has none)
        // supplier (and per-period costs) of a server's packages, for the quick-add dialog
        async supplierForServer(modem_name) {
            try { const res = await fetchWrapper.post(`${baseUrl}/admin/license/supplier-for-server`, { modem_name }); return res.supplier || null; }
            catch (_) { return null; }
        },
        // supplier + selling prices per period + (optional) the customer's balance, for the add-licence dialog
        async serverInfo(modem_name, customer_email = "") {
            try { const res = await fetchWrapper.post(`${baseUrl}/admin/license/supplier-for-server`, { modem_name, customer_email }); return { supplier: res.supplier || null, prices: res.prices || {}, customer: res.customer || null }; }
            catch (_) { return { supplier: null, prices: {}, customer: null }; }
        },
        async supplierInfo(uuid) {
            try { const res = await fetchWrapper.post(`${baseUrl}/admin/license/supplier-info`, { uuid }); return res.supplier || null; }
            catch (_) { return null; }
        },
        // supplier + customer (with selling prices per period) of a licence, for the Add time dialog
        async extendInfo(uuid) {
            try { const res = await fetchWrapper.post(`${baseUrl}/admin/license/supplier-info`, { uuid }); return { supplier: res.supplier || null, customer: res.customer || null }; }
            catch (_) { return { supplier: null, customer: null }; }
        },
        // book the cost price of a manual extension to the package supplier (SUPPLIER transaction)
        async supplierCredit(license_uuid, amount, note = "") {
            const alertStore = useAlertStore();
            try {
                const res = await fetchWrapper.post(`${baseUrl}/admin/license/supplier-credit`, { license_uuid, amount, note });
                alertStore.success(`Added $${Number(amount).toFixed(2)} to ${res.supplier?.name || "the supplier"}`)
                return true
            } catch (error) { alertStore.error(error); return false }
        },
        async getCustomers() {
            const alertStore = useAlertStore();
            try{
                const res = await fetchWrapper.get(`${baseUrl}/admin/user/get-customer-names`);
                this.customers = res.users
            } catch (error) {
                alertStore.error(error);
            }
        }
    },
});
